// Background removal (§11.12): one-click auto (B), key mode, refine brush, edge, view (W), fill, shadow,
// and the batch queue for several selected photos.
import { useEffect } from 'react';
import { Slider } from '../controls/Slider';
import { BrushControls, ToolButton } from './BrushControls';
import { useActive } from './useActive';
import { useUi } from '../../state/ui';
import { useLibrary } from '../../state/library';
import { useT, type MessageKey } from '../../i18n';
import { checkModels, useMatte } from '../../features/background/matte';
import { removeBackground } from '../../features/background/actions';
import type { BackgroundSettings, Rgb } from '../../model/types';

const VIEWS: MessageKey[] = ['bgViewChecker', 'bgViewWhite', 'bgViewBlack', 'bgViewMask', 'bgViewMatte'];
const hex = (c: Rgb) =>
  '#' +
  c
    .map((v) =>
      Math.round(Math.min(1, Math.max(0, v)) * 255)
        .toString(16)
        .padStart(2, '0'),
    )
    .join('');
const fromHex = (h: string): Rgb => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16) / 255) as Rgb;

export function BackgroundPanel() {
  const t = useT();
  const { id, settings, update } = useActive();
  const b = settings.background;
  const selected = useLibrary((s) => s.selected);
  const view = useUi((s) => s.bgView);
  const refineMode = useUi((s) => s.refineMode);
  const status = useMatte((m) => (id ? m.status[id] : undefined));
  const available = useMatte((m) => m.available);
  const model = useMatte((m) => m.model);
  const lastRun = useMatte((m) => m.lastRun);
  const batch = useMatte((m) => m.batch);
  useEffect(() => void checkModels(), []);

  const targets = selected.size > 1 ? [...selected] : id ? [id] : [];
  const setB = (fn: (b: BackgroundSettings) => void, final = true) => update((s) => fn(s.background), final);

  return (
    <div>
      <button
        data-testid="bg-remove"
        disabled={!targets.length || !!batch}
        onClick={() => void removeBackground(targets).catch(() => {})}
        style={{ width: '100%', minHeight: 36, background: 'var(--accent-fill)', color: 'var(--on-accent)', marginBottom: 6 }}
      >
        {targets.length > 1 ? t('bgRemoveN', { n: targets.length }) : t('bgRemove')}
      </button>
      {batch && (
        <div data-testid="bg-batch" style={{ marginBottom: 6 }}>
          <progress max={batch.total} value={batch.done} style={{ width: '100%' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
            <span>{t('progress', { done: batch.done, total: batch.total })}</span>
            <button
              onClick={() => useMatte.setState((m) => ({ batch: m.batch && { ...m.batch, cancel: true } }))}
            >
              {t('cancel')}
            </button>
          </div>
        </div>
      )}
      <div role="radiogroup" style={{ display: 'flex', gap: 4, marginBottom: 6 }}>
        {(['off', 'auto', 'key'] as const).map((m) => (
          <button
            key={m}
            role="radio"
            aria-checked={b.mode === m}
            data-testid={`bg-mode-${m}`}
            onClick={() => setB((x) => void (x.mode = m))}
            style={b.mode === m ? { background: 'var(--accent-fill)', color: 'var(--on-accent)' } : undefined}
          >
            {t(m === 'off' ? 'bgOff' : m === 'auto' ? 'bgAuto' : 'bgKey')}
          </button>
        ))}
      </div>
      {b.mode === 'auto' && (
        <div style={{ fontSize: 12, marginBottom: 6 }} data-testid="bg-status">
          <label style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 4 }}>
            {t('bgModel')}
            <select
              value={model}
              data-testid="bg-model"
              onChange={(e) => useMatte.getState().set({ model: e.target.value as typeof model })}
            >
              <option value="auto">{t('bgModelAuto')}</option>
              <option value="isnet" disabled={!available?.includes('isnet')}>
                {t('bgModelIsnet')}
              </option>
              <option value="u2netp" disabled={!available?.includes('u2netp')}>
                U²-Netp
              </option>
            </select>
          </label>
          {status?.state === 'loading' && <div>{t('bgLoading')}</div>}
          {status?.state === 'running' && <div>{t('bgRunning')}</div>}
          {status?.state === 'error' && (
            <div style={{ color: 'var(--danger)' }} data-testid="bg-error">
              {t('bgModelError')}
            </div>
          )}
          {status?.state === 'ready' && lastRun && (
            <div style={{ opacity: 0.7 }}>
              {t('bgDone', { model: lastRun.model, ep: lastRun.ep, s: (lastRun.totalMs / 1000).toFixed(1) })}
            </div>
          )}
        </div>
      )}
      {b.mode === 'key' && (
        <div style={{ marginBottom: 6 }}>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 4 }}>
            <button
              data-testid="bg-key-pick"
              onClick={() =>
                useUi.getState().set({ tool: useUi.getState().tool === 'bgKey' ? 'none' : 'bgKey' })
              }
            >
              {t('bgKeyPick')}
            </button>
            {b.key && (
              <span
                aria-label={t('bgKeyColor')}
                style={{
                  width: 20,
                  height: 20,
                  border: '1px solid var(--swatch-border)',
                  background: hex(
                    b.key.color.map((v) =>
                      v <= 0.0031308 ? 12.92 * v : 1.055 * v ** (1 / 2.4) - 0.055,
                    ) as Rgb,
                  ),
                }}
              />
            )}
          </div>
          {b.key ? (
            <>
              <Slider
                label="bgTolerance"
                testId="slider-bg-tolerance"
                value={b.key.tolerance}
                min={0}
                max={100}
                step={1}
                defaultValue={20}
                onChange={(v, f) => setB((x) => void (x.key!.tolerance = v), f)}
              />
              <Slider
                label="bgSoftness"
                testId="slider-bg-softness"
                value={b.key.softness}
                min={0}
                max={100}
                step={1}
                defaultValue={20}
                onChange={(v, f) => setB((x) => void (x.key!.softness = v), f)}
              />
              <label style={{ display: 'flex', gap: 6, fontSize: 12 }}>
                <input
                  type="checkbox"
                  data-testid="bg-key-connected"
                  checked={!!b.key.connected}
                  onChange={(e) => setB((x) => void (x.key!.connected = e.target.checked))}
                />
                {t('bgConnected')}
              </label>
            </>
          ) : (
            <div style={{ fontSize: 12, opacity: 0.8 }}>{t('bgKeyHint')}</div>
          )}
        </div>
      )}
      {b.mode !== 'off' && (
        <>
          <h4 style={{ margin: '8px 0 4px', fontSize: 12 }}>{t('bgRefine')}</h4>
          <ToolButton tool="bgRefine" label={t('bgRefineBrush')} testId="bg-refine-tool" />
          <div role="radiogroup" style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
            {(['keep', 'remove'] as const).map((m) => (
              <button
                key={m}
                role="radio"
                aria-checked={refineMode === m}
                onClick={() => useUi.getState().set({ refineMode: m })}
                style={refineMode === m ? { background: 'var(--accent-fill)', color: 'var(--on-accent)' } : undefined}
              >
                {t(m === 'keep' ? 'bgKeep' : 'bgRemoveStroke')}
              </button>
            ))}
          </div>
          <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 4 }}>{t('bgRefineHint')}</div>
          <BrushControls />
          <h4 style={{ margin: '8px 0 4px', fontSize: 12 }}>{t('bgEdge')}</h4>
          <Slider
            label="bgFeather"
            testId="slider-bg-feather"
            value={b.edge.feather}
            min={0}
            max={20}
            step={0.5}
            digits={1}
            onChange={(v, f) => setB((x) => void (x.edge.feather = v), f)}
          />
          <Slider
            label="bgShift"
            testId="slider-bg-shift"
            value={b.edge.shift}
            min={-10}
            max={10}
            step={0.5}
            digits={1}
            onChange={(v, f) => setB((x) => void (x.edge.shift = v), f)}
          />
          <Slider
            label="bgDecontaminate"
            testId="slider-bg-decontaminate"
            value={b.edge.decontaminate}
            min={0}
            max={100}
            step={1}
            onChange={(v, f) => setB((x) => void (x.edge.decontaminate = v), f)}
          />
          <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12, margin: '6px 0' }}>
            {t('bgView')}
            <select
              value={view}
              data-testid="bg-view"
              onChange={(e) => useUi.getState().set({ bgView: Number(e.target.value) })}
            >
              {VIEWS.map((k, i) => (
                <option key={k} value={i + 1}>
                  {t(k)}
                </option>
              ))}
            </select>
          </label>
          <h4 style={{ margin: '8px 0 4px', fontSize: 12 }}>{t('bgFill')}</h4>
          <div role="radiogroup" style={{ display: 'flex', gap: 4, alignItems: 'center', marginBottom: 4 }}>
            <button
              role="radio"
              aria-checked={b.fill.kind === 'transparent'}
              data-testid="bg-fill-transparent"
              onClick={() => setB((x) => void (x.fill = { ...x.fill, kind: 'transparent' }))}
            >
              {t('bgTransparent')}
            </button>
            <button
              role="radio"
              aria-checked={b.fill.kind === 'color' && b.fill.color.every((v) => v >= 0.999)}
              data-testid="bg-fill-white"
              onClick={() => setB((x) => void (x.fill = { kind: 'color', color: [1, 1, 1] }))}
            >
              {t('bgWhite')}
            </button>
            <input
              type="color"
              aria-label={t('bgColor')}
              data-testid="bg-fill-color"
              value={hex(b.fill.color)}
              onChange={(e) => setB((x) => void (x.fill = { kind: 'color', color: fromHex(e.target.value) }))}
            />
          </div>
          <div style={{ fontSize: 12, opacity: 0.8 }}>{t('jpegFlattenNote')}</div>
          <label style={{ display: 'flex', gap: 6, fontSize: 12, margin: '6px 0' }}>
            <input
              type="checkbox"
              data-testid="bg-shadow"
              checked={b.shadow.enabled}
              onChange={(e) => setB((x) => void (x.shadow.enabled = e.target.checked))}
            />
            {t('bgShadow')}
          </label>
          {b.shadow.enabled && (
            <>
              <Slider
                label="opacity"
                testId="slider-bg-shadow-opacity"
                value={b.shadow.opacity}
                min={0}
                max={100}
                step={1}
                defaultValue={40}
                onChange={(v, f) => setB((x) => void (x.shadow.opacity = v), f)}
              />
              <Slider
                label="bgShadowBlur"
                testId="slider-bg-shadow-blur"
                value={b.shadow.blur}
                min={0}
                max={100}
                step={1}
                defaultValue={30}
                onChange={(v, f) => setB((x) => void (x.shadow.blur = v), f)}
              />
              <Slider
                label="bgShadowOffset"
                testId="slider-bg-shadow-offset"
                value={b.shadow.offsetY}
                min={-10}
                max={10}
                step={0.1}
                digits={1}
                defaultValue={2}
                onChange={(v, f) => setB((x) => void (x.shadow.offsetY = v), f)}
              />
            </>
          )}
        </>
      )}
    </div>
  );
}
